import { Report, Template } from "PFApp/booking/types/manage_reports";
import PageTabs from "PFComponents/page/page_tabs";
import SidePanel from "PFComponents/side_panel/side_panel";
import { useReports } from "PFCore/hooks/queries/bookings/manage_reports/use_reports";
import { useTemplates } from "PFCore/hooks/queries/bookings/manage_reports/use_templates";
import React from "react";
import { useTranslation } from "react-i18next";

import css from "../manage_reports.module.scss";
import ReportsTab from "./reports_tab/reports_tab";
import TemplatesTab from "./templates_tab/templates_tab";
import { useManageReportsTabs } from "./use_manage_reports_tabs";

const REFETCH_INTERVAL_MS = 2 * 60 * 1000;

const TAB_COMPONENTS = {
  reports: ReportsTab,
  templates: TemplatesTab
};

type ManageReportsSidePanelProps = {
  show: boolean;
  onClose: () => void;
};

export const ManageReportsSidePanel = ({ show, onClose }: ManageReportsSidePanelProps): JSX.Element => {
  const { t } = useTranslation("bookingModule");
  const { currentTab, tabs, handleClickTab } = useManageReportsTabs();
  const { data: reportsData, isLoading: reportsIsLoading } = useReports({
    enabled: show,
    refetchInterval: REFETCH_INTERVAL_MS
  });
  const { data: templatesData, isLoading: templatesIsLoading } = useTemplates({
    enabled: show,
    refetchInterval: REFETCH_INTERVAL_MS
  });

  const TabComponent = TAB_COMPONENTS[currentTab];

  const counters = {
    reports: reportsData ? reportsData.meta.total : 0,
    templates: templatesData ? templatesData.meta.total : 0
  };

  const tabComponentProps = {
    isLoading: currentTab === "reports" ? reportsIsLoading : templatesIsLoading,
    reports: (reportsData?.entries as Report[]) ?? [],
    templates: (templatesData?.entries as Template[]) ?? [],
    onClose
  };

  return (
    <SidePanel
      title={t("reports.manageReports.title")}
      show={show}
      onClose={onClose}
      size="large"
      classes={{ show: css.sidePanelShow }}
    >
      <PageTabs
        rootClassName={css.tabs}
        items={tabs}
        active={currentTab}
        handleClick={handleClickTab}
        counters={counters}
      />
      <TabComponent {...tabComponentProps} />
    </SidePanel>
  );
};
